import { useCallback, useEffect, useState } from 'react'
import { adminClient } from '../../api'
import type { AdminBotStatusV1 } from '../../api/adminTypes'
import { AdminNotice, EmailLookupForm } from './adminShared'
import { describeAdminError, useEmailParam } from './adminHelpers'
/**
 * Equivalent of legacy `AdminBotUserComponent` (route `admin/bot/user`,
 * contract #21 — the legacy v1 shape). `statuses` is always `[]` on this
 * endpoint (legacy quirk: the underlying botcontrol call was a canned 409
 * even in production) — shown as "not available" rather than hidden, so an
 * admin used to the legacy page is not confused by its absence.
 */
export function AdminBotUserPage() {
  const [email, setEmail] = useEmailParam()
  const [status, setStatus] = useState<AdminBotStatusV1 | null>(null)
  const [feedback, setFeedback] = useState<ReturnType<typeof describeAdminError> | undefined>()

  const load = useCallback((targetEmail: string) => {
    setStatus(null)
    setFeedback(undefined)
    adminClient
      .botStatusV1(targetEmail)
      .then((result) => setStatus(result))
      .catch((err) => setFeedback(describeAdminError(err)))
  }, [])

  useEffect(() => {
    if (email) load(email)
    else setStatus(null)
  }, [email, load])

  return (
    <div>
      <div className="portal-page-title">
        <h1>Admin — bot user (v1)</h1>
      </div>
      <div className="portal-card">
        <EmailLookupForm email={email} onSubmit={setEmail} />
        <AdminNotice feedback={feedback} />
        {!email && <p className="portal-text-muted">Enter a user e-mail to look up their v1 bot status.</p>}
        {email && status === null && !feedback && <p>Loading…</p>}
        {email && status !== null && (
          <dl className="portal-kv">
            <dt>Subscription status</dt>
            <dd>{status.subscriptionStatus}</dd>
            <dt>Payment status</dt>
            <dd>{status.paymentStatus}</dd>
            <dt>Test user status</dt>
            <dd>{status.testUserStatus}</dd>
            <dt>Subscription plan</dt>
            <dd>{status.details.subscription?.plan ?? 'none'}</dd>
            <dt>Subscription expires</dt>
            <dd>{status.details.subscription?.expiresAt ?? '—'}</dd>
            <dt>Products</dt>
            <dd>{status.details.products?.length ?? 0}</dd>
            <dt>Botcontrol statuses</dt>
            <dd className="portal-text-muted">not available (legacy quirk — the underlying call was never functional)</dd>
          </dl>
        )}
      </div>
    </div>
  )
}
